import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Sparkles, Mail, Eye, UserPlus, MessageCircle, Phone, Copy, RefreshCw, AlertCircle, Flame } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

const CHANNEL_ICON = {
  email: Mail,
  linkedin_view: Eye,
  linkedin_connect: UserPlus,
  linkedin_dm: MessageCircle,
  voicemail: Phone,
  task_manual: AlertCircle,
};

const CHANNEL_LABEL = {
  email: "Email",
  linkedin_view: "LinkedIn · view profile",
  linkedin_connect: "LinkedIn · connect request",
  linkedin_dm: "LinkedIn · DM",
  voicemail: "Voicemail drop",
  task_manual: "Manual task",
};

const KIND_LABEL = {
  insight: "Commercial Insight",
  case_study: "Case Study",
  trigger: "Trigger Event",
  meeting_ask: "Meeting Ask",
  follow_up: "Follow-up",
  breakup: "Breakup",
  nurture: "Nurture",
};

export default function SequencePreviewModal({ open, onClose, lead, autoGenerate }) {
  const [loadingInit, setLoadingInit] = useState(false);
  const [data, setData] = useState(null);
  const [stepStatus, setStepStatus] = useState({}); // order → 'pending' | 'done' | 'error'
  const runIdRef = useRef(0);

  useEffect(() => {
    if (!open) return;
    setData(null);
    setStepStatus({});
    if (autoGenerate && lead) {
      generate(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, lead?.lead_id]);

  const draftEmailSteps = async (initData, runId) => {
    // Fire all email-step drafts in parallel — each call < 15s, well under ingress 60s limit
    const emailSteps = (initData.steps || []).filter((s) => s.channel === "email");
    setStepStatus((prev) => {
      const next = { ...prev };
      emailSteps.forEach((s) => { next[s.order] = "pending"; });
      return next;
    });

    await Promise.all(
      emailSteps.map(async (s) => {
        try {
          const { data: stepData } = await api.post(
            `/ai/preview-sequence/${initData.lead_id}/step/${s.order}`,
            null,
            { timeout: 60000 }
          );
          if (runIdRef.current !== runId) return; // stale
          setData((prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              steps: prev.steps.map((st) =>
                st.order === s.order
                  ? { ...st, subject: stepData.subject, body_text: stepData.body_text, error: stepData.error }
                  : st
              ),
            };
          });
          setStepStatus((prev) => ({ ...prev, [s.order]: stepData.error ? "error" : "done" }));
        } catch (err) {
          if (runIdRef.current !== runId) return;
          setStepStatus((prev) => ({ ...prev, [s.order]: "error" }));
          setData((prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              steps: prev.steps.map((st) =>
                st.order === s.order
                  ? { ...st, subject: "[draft failed]", body_text: err?.response?.data?.detail || String(err), error: true }
                  : st
              ),
            };
          });
        }
      })
    );
  };

  const generate = async (force = false) => {
    if (!lead) return;
    const runId = ++runIdRef.current;
    setLoadingInit(true);
    setData(null);
    setStepStatus({});
    try {
      const { data: init } = await api.post(
        `/ai/preview-sequence/${lead.lead_id}/init`,
        null,
        { params: { force_research: force }, timeout: 60000 }
      );
      if (runIdRef.current !== runId) return;
      setData(init);
      setLoadingInit(false);
      toast.success("Research ready · drafting emails…");
      draftEmailSteps(init, runId);
    } catch (err) {
      setLoadingInit(false);
      toast.error(err?.response?.data?.detail || "Preview init failed");
    }
  };

  const regenerateOne = async (order) => {
    if (!data) return;
    const runId = runIdRef.current;
    setStepStatus((prev) => ({ ...prev, [order]: "pending" }));
    try {
      const { data: stepData } = await api.post(
        `/ai/preview-sequence/${data.lead_id}/step/${order}`,
        null,
        { timeout: 60000 }
      );
      if (runIdRef.current !== runId) return;
      setData((prev) => ({
        ...prev,
        steps: prev.steps.map((st) =>
          st.order === order
            ? { ...st, subject: stepData.subject, body_text: stepData.body_text, error: stepData.error }
            : st
        ),
      }));
      setStepStatus((prev) => ({ ...prev, [order]: stepData.error ? "error" : "done" }));
    } catch (err) {
      setStepStatus((prev) => ({ ...prev, [order]: "error" }));
      toast.error(err?.response?.data?.detail || "Regenerate failed");
    }
  };

  const copyStep = async (step) => {
    try {
      const text = step.channel === "email"
        ? `Subject: ${step.subject || ""}\n\n${step.body_text || ""}`
        : step.task_note || "";
      await navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Copy failed");
    }
  };

  const emailDone = data ? data.steps.filter((s) => s.channel === "email" && stepStatus[s.order] === "done").length : 0;
  const emailTotal = data ? data.steps.filter((s) => s.channel === "email").length : 0;
  const allDone = data && emailDone === emailTotal && emailTotal > 0;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" style={{ borderRadius: 2 }} data-testid="sequence-preview-modal">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl tracking-tight" style={{ fontWeight: 400 }}>
            AI Sequence Preview · {lead?.full_name}
          </DialogTitle>
          <p className="text-xs text-muted-foreground mt-1">
            {lead?.title} · {lead?.company} · {lead?.industry}
          </p>
        </DialogHeader>

        <div className="space-y-5">
          {!data && !loadingInit && (
            <div className="border border-dashed border-border p-8 text-center" style={{ borderRadius: 2 }}>
              <Sparkles className="h-6 w-6 mx-auto mb-3 text-muted-foreground" strokeWidth={1.5} />
              <p className="text-sm text-muted-foreground mb-4">
                Generate all 11 outreach steps (7 emails + LinkedIn touches + voicemail) using your saved value proposition,
                trigger events, and industry analysis. Nothing is sent — preview only.
              </p>
              <Button onClick={() => generate(false)} data-testid="seq-preview-generate" style={{ borderRadius: 2 }}>
                <Sparkles className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} /> Generate preview
              </Button>
            </div>
          )}

          {loadingInit && (
            <div className="border border-border p-8 text-center" style={{ borderRadius: 2 }}>
              <RefreshCw className="h-6 w-6 mx-auto mb-3 text-muted-foreground animate-spin" strokeWidth={1.5} />
              <p className="text-sm text-muted-foreground">
                Running AI research…
                <br />
                <span className="text-xs">Generating company summary, industry analysis, trigger events &amp; pain points (~10s).</span>
              </p>
            </div>
          )}

          {data && (
            <>
              {/* Research summary header */}
              <div className="bg-secondary/30 border border-border p-4 space-y-2" style={{ borderRadius: 2 }} data-testid="seq-preview-research">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="overline">AI research seed</div>
                  <div className="flex items-center gap-3">
                    {emailTotal > 0 && (
                      <span className="text-[10px] uppercase tracking-widest font-mono text-muted-foreground" data-testid="seq-preview-progress">
                        {emailDone}/{emailTotal} emails drafted{allDone ? " · done" : "…"}
                      </span>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => generate(true)} disabled={loadingInit} data-testid="seq-preview-regen-research" style={{ borderRadius: 2 }}>
                      <RefreshCw className="h-3 w-3 mr-1" strokeWidth={1.5} /> Re-run research
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                  <div><span className="text-xs uppercase tracking-widest text-muted-foreground font-mono">Trigger:</span> {data.research?.trigger_event || "—"}</div>
                  <div><span className="text-xs uppercase tracking-widest text-muted-foreground font-mono">Angle:</span> {data.research?.outreach_angle || "—"}</div>
                  <div className="md:col-span-2"><span className="text-xs uppercase tracking-widest text-muted-foreground font-mono">Pain points:</span> {(data.research?.pain_points || []).join(" · ") || "—"}</div>
                  <div className="md:col-span-2"><span className="text-xs uppercase tracking-widest text-muted-foreground font-mono">Cost categories:</span> {(data.research?.cost_categories || []).join(" · ") || "—"}</div>
                </div>
                {data.value_proposition && (
                  <details className="mt-2">
                    <summary className="text-xs uppercase tracking-widest text-muted-foreground font-mono cursor-pointer">Your value proposition (seed) ▾</summary>
                    <p className="text-xs italic mt-2 leading-relaxed">{data.value_proposition}</p>
                  </details>
                )}
              </div>

              {/* Step-by-step preview */}
              <div className="space-y-3" data-testid="seq-preview-steps">
                {data.steps.map((step) => {
                  const Icon = CHANNEL_ICON[step.channel] || Mail;
                  const isEmail = step.channel === "email";
                  const status = stepStatus[step.order];
                  return (
                    <div
                      key={step.order}
                      className={`border ${isEmail ? "border-border" : "border-dashed border-border"} bg-card p-4`}
                      style={{ borderRadius: 2 }}
                      data-testid={`seq-step-${step.order}`}
                    >
                      <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[10px] uppercase tracking-widest font-mono px-1.5 py-0.5 bg-foreground text-background" style={{ borderRadius: 2 }}>
                            Step {step.order}
                          </span>
                          <Icon className="h-3.5 w-3.5" strokeWidth={1.5} />
                          <span className="text-xs uppercase tracking-widest text-muted-foreground font-mono">
                            {CHANNEL_LABEL[step.channel] || step.channel}
                          </span>
                          {isEmail && KIND_LABEL[step.kind] && (
                            <span className="text-[10px] uppercase tracking-widest font-mono px-1.5 py-0.5 bg-secondary text-foreground" style={{ borderRadius: 2 }}>
                              {KIND_LABEL[step.kind]}
                            </span>
                          )}
                          {step.kind === "trigger" && <Flame className="h-3 w-3 text-primary" strokeWidth={1.5} />}
                          <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono">
                            Day +{step.cumulative_days}
                          </span>
                        </div>
                        <div className="flex items-center gap-3">
                          {isEmail && (
                            <button
                              onClick={() => regenerateOne(step.order)}
                              disabled={status === "pending"}
                              className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 disabled:opacity-50"
                              data-testid={`seq-step-regen-${step.order}`}
                              title="Re-draft this email only"
                            >
                              <RefreshCw className={`h-3 w-3 ${status === "pending" ? "animate-spin" : ""}`} strokeWidth={1.5} /> Re-draft
                            </button>
                          )}
                          <button
                            onClick={() => copyStep(step)}
                            className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
                            data-testid={`seq-step-copy-${step.order}`}
                          >
                            <Copy className="h-3 w-3" strokeWidth={1.5} /> Copy
                          </button>
                        </div>
                      </div>

                      {isEmail ? (
                        <>
                          {status === "pending" && (
                            <div className="flex items-center gap-2 text-xs text-muted-foreground py-3">
                              <RefreshCw className="h-3 w-3 animate-spin" strokeWidth={1.5} /> Drafting…
                            </div>
                          )}
                          {step.error ? (
                            <div className="text-xs text-destructive flex items-center gap-1">
                              <AlertCircle className="h-3 w-3" strokeWidth={1.5} /> Draft failed: {step.body_text}
                            </div>
                          ) : step.subject ? (
                            <>
                              <div className="text-sm font-medium mb-1.5" data-testid={`seq-step-subject-${step.order}`}>
                                {step.subject}
                              </div>
                              <pre className="whitespace-pre-wrap text-sm text-foreground/90 font-sans leading-relaxed">
                                {step.body_text}
                              </pre>
                            </>
                          ) : null}
                        </>
                      ) : (
                        <div className="text-sm italic text-foreground/90 leading-relaxed">
                          {step.task_note || step.subject || "(no template)"}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
